import React from 'react';
import { motion } from 'framer-motion';
import { FiAward, FiZap, FiTool, FiBookOpen, FiCheck } from 'react-icons/fi';
import { USER_PROFICIENCY_LEVELS } from '../../../utils/proficiencyMap';

interface ReviewStepProps {
    allData: {
        discovery: {
            enjoyDoing: string[];
            haveDone: string[];
            wantNow: string[];
        };
        confirmation: {
            confirmedSkills: Array<{
                name: string;
                category: string;
                proficiency: number;
            }>;
        };
        experience: {
            workFrequency?: 'daily' | 'weekly' | 'occasional' | 'first-time';
            environment?: 'indoor' | 'outdoor' | 'both';
            nightWork?: boolean;
            emergencyWork?: boolean;
            shortNotice?: boolean;
        };
    };
    onBack: () => void;
    onSave: () => void;
    saving: boolean;
}

const ReviewStep: React.FC<ReviewStepProps> = ({ allData, onBack, onSave, saving }) => {
    const categoryColors: Record<string, string> = {
        professional: '#D4AF37',
        practical: '#4A90E2',
        physical: '#7ED321',
        soft: '#9013FE'
    };

    const frequencyLabels: Record<string, string> = {
        daily: 'Every Day',
        weekly: 'Weekly',
        occasional: 'Sometimes',
        'first-time': 'New to This'
    };

    const environmentLabels: Record<string, string> = {
        indoor: 'Indoors',
        outdoor: 'Outdoors',
        both: 'Both Indoor & Outdoor'
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-white mb-2">
                    Review Your Talent Profile
                </h2>
                <p className="text-white/60">
                    Everything looks good? You can always add more skills later!
                </p>
            </div>

            {/* Skills Summary */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-premium-gold uppercase tracking-wider">
                        Your Skills ({allData.confirmation.confirmedSkills.length})
                    </h3>
                </div>

                <div className="grid gap-3">
                    {allData.confirmation.confirmedSkills.map((skill) => {
                        const profLevel = USER_PROFICIENCY_LEVELS.find(p => p.value === skill.proficiency);

                        return (
                            <motion.div
                                key={skill.name}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-white/20 transition-all"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <h4 className="text-white font-bold">{skill.name}</h4>
                                            <span
                                                className="px-2 py-0.5 rounded text-xs font-medium capitalize"
                                                style={{
                                                    backgroundColor: `${categoryColors[skill.category]}20`,
                                                    color: categoryColors[skill.category],
                                                    border: `1px solid ${categoryColors[skill.category]}40`
                                                }}
                                            >
                                                {skill.category}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 flex-shrink-0 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center text-premium-gold text-2xl">
                                                {skill.proficiency === 1 && <FiBookOpen />}
                                                {skill.proficiency === 2 && <FiTool />}
                                                {skill.proficiency === 3 && <FiAward />}
                                                {skill.proficiency === 4 && <FiZap />}
                                            </div>
                                            <div>
                                                <p className="text-sm text-white/80 font-medium">
                                                    {profLevel?.userLabel}
                                                </p>
                                                <p className="text-xs text-white/50">
                                                    {profLevel?.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Work Patterns Summary */}
            <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                <h3 className="text-sm font-bold text-premium-gold uppercase tracking-wider mb-4">
                    Work Patterns & Preferences
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    {allData.experience.workFrequency && (
                        <div>
                            <p className="text-xs text-white/50 mb-1">Frequency</p>
                            <p className="text-white font-medium">
                                {frequencyLabels[allData.experience.workFrequency]}
                            </p>
                        </div>
                    )}
                    {allData.experience.environment && (
                        <div>
                            <p className="text-xs text-white/50 mb-1">Environment</p>
                            <p className="text-white font-medium">
                                {environmentLabels[allData.experience.environment]}
                            </p>
                        </div>
                    )}
                </div>

                {/* Availability Badges */}
                {(allData.experience.nightWork || allData.experience.emergencyWork || allData.experience.shortNotice) && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-xs text-white/50 mb-2">Availability</p>
                        <div className="flex flex-wrap gap-2">
                            {allData.experience.nightWork && (
                                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs border border-blue-500/30">
                                    🌙 Night Work
                                </span>
                            )}
                            {allData.experience.emergencyWork && (
                                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-xs border border-red-500/30">
                                    🚨 Emergency Work
                                </span>
                            )}
                            {allData.experience.shortNotice && (
                                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-xs border border-yellow-500/30">
                                    ⚡ Short Notice
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Discovery Insights (Collapsed) */}
            {(allData.discovery.enjoyDoing.length > 0 ||
                allData.discovery.haveDone.length > 0 ||
                allData.discovery.wantNow.length > 0) && (
                    <details className="p-4 bg-white/5 border border-white/10 rounded-xl">
                        <summary className="text-sm font-bold text-premium-gold uppercase tracking-wider cursor-pointer">
                            Your Original Answers ▼
                        </summary>
                        <div className="mt-4 space-y-3">
                            {allData.discovery.enjoyDoing.length > 0 && (
                                <div>
                                    <p className="text-xs text-white/50 mb-2">What You Enjoy:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {allData.discovery.enjoyDoing.map(item => (
                                            <span key={item} className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs border border-green-500/30">
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {allData.discovery.haveDone.length > 0 && (
                                <div>
                                    <p className="text-xs text-white/50 mb-2">What You've Done:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {allData.discovery.haveDone.map(item => (
                                            <span key={item} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs border border-blue-500/30">
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {allData.discovery.wantNow.length > 0 && (
                                <div>
                                    <p className="text-xs text-white/50 mb-2">What You Want:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {allData.discovery.wantNow.map(item => (
                                            <span key={item} className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs border border-purple-500/30">
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </details>
                )}

            {/* Navigation */}
            <div className="flex justify-between pt-4">
                <button
                    onClick={onBack}
                    disabled={saving}
                    className="px-6 py-2 text-white/70 hover:text-white transition-colors disabled:opacity-50"
                >
                    ← Back
                </button>
                <button
                    onClick={onSave}
                    disabled={saving}
                    className={`px-8 py-3 rounded-lg font-bold transition-all flex items-center gap-2 ${saving
                        ? 'bg-white/10 text-white/40 cursor-not-allowed'
                        : 'bg-gradient-to-r from-green-600 to-green-500 text-white hover:shadow-[0_0_20px_rgba(34,197,94,0.4)] transform hover:scale-105'
                        }`}
                >
                    {saving ? (
                        <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <FiCheck size={20} />
                            Save My Talent Profile
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default ReviewStep;
